import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ApolloClient,
  NormalizedCacheObject,
  useLazyQuery,
} from "@apollo/client";
import {
  getCartIdFromCookie,
  saveCartCookie,
  deleteCartCookie,
} from "helpers/cart";
import {
  CartQuery,
  CartFragment,
  CartDocument,
  CartQueryVariables,
  GetCartFromUserQuery,
  GetCartFromUserDocument,
  GetCartFromUserQueryVariables,
  useAdjustCartItemVariantMutation,
  useAdjustCartItemVariantSubscriptionMutation,
  CreateCartFromOrderMutation,
  useMergeCartsMutation,
  usePersistTemporaryCartMutation,
  AdjustCartItemVariantSubscriptionInput,
  V3ValidationErrorFragment,
} from "services/graphql/generated";
import { CartContextInterface } from "../cart";
import { booleanFilter } from "@pepdirect/v3/helpers/booleanFilter";

export function useCartProvider(
  client: ApolloClient<NormalizedCacheObject>,
  currentUserId: string | null
): {
  cartContextValue: CartContextInterface;
} {
  const [cart, setCart] = useState<CartFragment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // V3 mutations
  const [adjustCartItemVariantMutation] = useAdjustCartItemVariantMutation({
    client,
  });
  const [adjustCartItemVariantSubscription] =
    useAdjustCartItemVariantSubscriptionMutation({ client });

  const [getCartFromId] = useLazyQuery<CartQuery, CartQueryVariables>(
    CartDocument,
    { client }
  );
  const [getCartFromUserId] = useLazyQuery<
    GetCartFromUserQuery,
    GetCartFromUserQueryVariables
  >(GetCartFromUserDocument, { client });
  const [mergeCarts] = useMergeCartsMutation({ client });
  const [persistTemporaryCart] = usePersistTemporaryCartMutation({ client });

  const fetchCart = useCallback(
    async (cartIdFromCookie: string) => {
      try {
        const { data } = await getCartFromId({
          variables: { id: cartIdFromCookie },
        });
        setCart(data?.cart || null);
      } catch {
        setCart(null);
      } finally {
        setLoading(false);
      }
    },
    [getCartFromId]
  );

  const getCartFromUser = useCallback(async () => {
    try {
      const { data } = await getCartFromUserId();
      setCart(data?.currentUser?.cart || null);
    } catch {
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, [getCartFromUserId]);

  /**
   * Returns the current Cart ID, either from the cart object or from the cookie.
   * @returns The cart ID or null if no cart ID is found.
   */
  const getCartId = () => {
    const cartIdFromCookie = getCartIdFromCookie();
    if (cart?.id) return cart.id;
    if (cartIdFromCookie) return cartIdFromCookie;
    return null;
  };

  const refreshCart = useCallback(() => {
    const cartIdFromCookie = getCartIdFromCookie();
    // get cart with a cart id
    if (cartIdFromCookie) {
      fetchCart(cartIdFromCookie);
    }

    // no cart id: get cart from current user
    if (!cartIdFromCookie && currentUserId) {
      getCartFromUser();
    }

    // no cart id or current user: no cart
    if (!cartIdFromCookie && !currentUserId) {
      setCart(null);
      setLoading(false);
    }
  }, [currentUserId, fetchCart, getCartFromUser]);

  /**
   * Adds an quantity of GTINs to the cart.
   * @param input The CartItemUpdateDto containing the variantGtin, quantity, and subscription interval (if applicable)
   */
  const addToCart = async (input: AdjustCartItemVariantSubscriptionInput) => {
    if (!input.variantGtin)
      throw new Error("No variantGtin, could not add product.");
    const cartId = getCartId();
    // productId is the ProductUniqueId
    const { variantGtin, subscriptionIntervalInDays } = input;

    // Check if item already exists in cart and sum quantities
    const existingItem = cart?.variantItems?.find(
      (item) => item?.variant.gtin === variantGtin
    );
    const quantity = existingItem
      ? existingItem.quantity + input.quantity
      : input.quantity;

    try {
      if (subscriptionIntervalInDays) {
        // Use subscription mutation if subscription data exists
        const { data } = await adjustCartItemVariantSubscription({
          variables: {
            cartId,
            variantGtin,
            quantity,
            subscriptionIntervalInDays,
          },
        });
        setCart(data?.adjustCartItemVariantSubscription || null);
      } else {
        // Use regular mutation for non-subscription items
        const { data } = await adjustCartItemVariantMutation({
          variables: {
            cartId,
            input: {
              variantGtin,
              quantity,
            },
          },
        });
        setCart(data?.adjustCartItemVariant || null);
      }
    } catch (e) {
      // logged in errorLink
    } finally {
      setLoading(false);
    }
  };

  const mergeCart = async (
    cartFromOrder: CreateCartFromOrderMutation["createTemporaryCartFromOrder"]
  ) => {
    if (cartFromOrder) {
      const { id } = cartFromOrder;
      if (cart?.id) {
        const { data } = await mergeCarts({
          variables: { destCartId: cart?.id, srcCartId: id },
        });
        fetchCart(data?.mergeCarts?.id);
        return;
      }
      const { data } = await persistTemporaryCart({
        variables: { srcCartId: id },
      });
      fetchCart(data?.persistTemporaryCart?.id);
    }
  };

  /**
   * Updates an item in the cart.
   * @param input The CartItemUpdateDto containing the variantGtin, quantity, and subscription interval (if applicable)
   */
  const updateCart = async (input: AdjustCartItemVariantSubscriptionInput) => {
    if (!input.variantGtin) {
      throw new Error("No variantGtin, could not update product.");
    }
    const cartId = getCartId();
    const { variantGtin, quantity, subscriptionIntervalInDays } = input;

    try {
      if (subscriptionIntervalInDays) {
        // Use subscription mutation if subscription data exists
        const { data } = await adjustCartItemVariantSubscription({
          variables: {
            cartId,
            variantGtin,
            quantity,
            subscriptionIntervalInDays,
          },
        });
        setCart(data?.adjustCartItemVariantSubscription || null);
      } else {
        // Use regular mutation for non-subscription items
        const { data } = await adjustCartItemVariantMutation({
          variables: {
            cartId,
            input: {
              variantGtin,
              quantity,
            },
          },
        });
        setCart(data?.adjustCartItemVariant || null);
      }
    } catch (e) {
      // logged in errorLink
      console.error("Error updating item in cart:", e);
    } finally {
      setLoading(false);
    }
  };

  // refresh cart on currentUserId and tab changes
  useEffect(() => {
    refreshCart();
    /* Event listener to refresh cart after changing tabs */
    /* TODO: debounce? */
    const refreshIfTabHasChanged = () => {
      if (document?.visibilityState === "visible") refreshCart();
    };
    window?.addEventListener("visibilitychange", refreshIfTabHasChanged);
    return function removeFocusRefresh() {
      window?.removeEventListener("visibilitychange", refreshIfTabHasChanged);
    };
  }, [currentUserId, refreshCart]);

  // update cookies depending on cart id changes
  useEffect(() => {
    if (!loading) {
      if (cart?.id) {
        if (cart.id !== getCartIdFromCookie()) {
          saveCartCookie(cart.id);
        }
      } else {
        deleteCartCookie();
      }
    }
  }, [cart?.id, loading]);

  const validationErrors = useMemo<V3ValidationErrorFragment[]>(() => {
    return (cart?.validationErrors?.filter(booleanFilter) ||
      []) as V3ValidationErrorFragment[];
  }, [cart?.validationErrors]);

  // TODO: consider using recoil.js when it is stable
  const cartContextValue: CartContextInterface = {
    cart,
    loading,
    validationErrors,
    refreshCart,
    addCartItem: addToCart,
    updateCartItem: updateCart,
    getSize: () => {
      return loading
        ? undefined
        : cart?.variantItems
            ?.filter(booleanFilter)
            .map(({ quantity }) => quantity)
            .reduce((a, b) => a + b, 0) || 0;
    },
    mergeCart,
    getCartId,
  };

  return { cartContextValue };
}
